import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

// Safe date parsing function to handle cross-browser compatibility
function safeParseDate(dateString) {
  if (!dateString) return null;
  
  // Try standard parsing first
  const date = new Date(dateString);
  if (!isNaN(date.getTime())) return date;
  
  // If that fails, try more specific formats
  try {
    // For format like "2025-04-01 00:00:00 +0000"
    if (typeof dateString === 'string') {
      // Replace space with T and standardize timezone for better browser compatibility
      const dateWithT = dateString.replace(' ', 'T');
      const newDate = new Date(dateWithT);
      if (!isNaN(newDate.getTime())) return newDate;
      
      // Try with explicit timezone handling
      const cleanedDate = dateString.replace(/(\+\d{4})$/, 'Z');
      const dateWithTZ = cleanedDate.replace(' ', 'T');
      const tzDate = new Date(dateWithTZ);
      if (!isNaN(tzDate.getTime())) return tzDate;
      
      // If still failing, try more aggressive approach - just use the date part
      const parts = dateString.split(' ');
      if (parts.length >= 2) {
        const [year, month, day] = parts[0].split('-').map(Number);
        // JS months are 0-indexed
        return new Date(year, month - 1, day);
      }
    }
  } catch (e) {
    console.error('Error in safeParseDate:', e);
  }
  
  return null;
}

const CostBreakdown = ({ invoiceData, selectedInvoice }) => {
  const [chartData, setChartData] = useState([]);
  const [serviceTypes, setServiceTypes] = useState([]);
  const [totalCosts, setTotalCosts] = useState({});
  const [availablePeriods, setAvailablePeriods] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  
  // Extract unique periods from invoice data
  useEffect(() => {
    if (!invoiceData || invoiceData.length === 0) return;
    
    // Extract unique periods (months) from the invoice data
    const periodSet = new Set();
    invoiceData.forEach(row => {
      if (row.start) {
        const startDate = safeParseDate(row.start);
        if (startDate) {
          // Format as YYYY-MM for month-based periods (use UTC to avoid timezone shift)
          const periodKey = `${startDate.getUTCFullYear()}-${String(startDate.getUTCMonth() + 1).padStart(2, '0')}`;
          periodSet.add(periodKey);
        }
      }
    });
    
    // Convert to array and sort (most recent first)
    const periods = Array.from(periodSet).sort().reverse();
    setAvailablePeriods(periods);
    
    // Set the most recent period as default (only if not already set or if current selection is invalid)
    if (periods.length > 0) {
      if (selectedPeriod === null || !periods.includes(selectedPeriod)) {
        setSelectedPeriod(periods[0]);
      }
    }
  }, [invoiceData, selectedPeriod]);
  
  useEffect(() => {
    if (!invoiceData || invoiceData.length === 0 || !selectedPeriod) return;
    
    // Define service types to exclude
    const excludedServiceTypes = [
      "Premier Support Subscription", 
      "Discounts", 
      "Commitment Deal Surcharge"
    ];
    
    // Filter by selected period first (use UTC to avoid timezone shift)
    const periodFilteredData = invoiceData.filter(row => {
      if (!row.start) return false;
      const startDate = safeParseDate(row.start);
      if (!startDate) return false;
      const rowPeriod = `${startDate.getUTCFullYear()}-${String(startDate.getUTCMonth() + 1).padStart(2, '0')}`;
      return rowPeriod === selectedPeriod;
    });
    
    // Filter out excluded service types
    const filteredData = periodFilteredData.filter(row => 
      !excludedServiceTypes.includes(row.product) && row.product // Make sure product exists
    );
    
    // Process the data for the chart
    const dailyCosts = processDailyServiceCosts(filteredData);
    
    // Extract unique service types
    const uniqueServiceTypes = new Set();
    filteredData.forEach(row => {
      if (row.product) uniqueServiceTypes.add(row.product);
    });
    const serviceTypesArray = Array.from(uniqueServiceTypes);
    
    // Convert the daily costs object to an array for the chart
    const processedChartData = Object.keys(dailyCosts).map(date => {
      const entry = { date };
      
      // Add cost for each service type
      serviceTypesArray.forEach(serviceType => {
        entry[serviceType] = dailyCosts[date][serviceType] || 0;
      });
      
      // Calculate total for the day
      entry.total = serviceTypesArray.reduce(
        (sum, type) => sum + (dailyCosts[date][type] || 0), 
        0
      );
      
      return entry;
    });
    
    // Sort the chart data by date
    processedChartData.sort((a, b) => new Date(a.date) - new Date(b.date));
    
    // Calculate total costs by service type
    const calculatedTotalCosts = {};
    serviceTypesArray.forEach(type => {
      calculatedTotalCosts[type] = processedChartData.reduce(
        (sum, item) => sum + (item[type] || 0), 
        0
      );
    });
    
    // Ensure total cost is calculated properly
    calculatedTotalCosts.total = serviceTypesArray.reduce(
      (sum, type) => sum + (calculatedTotalCosts[type] || 0),
      0
    );
    
    setChartData(processedChartData);
    setServiceTypes(serviceTypesArray);
    setTotalCosts(calculatedTotalCosts);
  }, [invoiceData, selectedPeriod]);
  
  // Function to process data into daily costs by service type
  function processDailyServiceCosts(data) {
    const dailyCosts = {};
    
    // Define service types to exclude
    const excludedServiceTypes = [
      "Premier Support Subscription", 
      "Discounts", 
      "Commitment Deal Surcharge"
    ];
    
    data.forEach(row => {
      if (!row.start || !row.end || row.USD == null || row.USD === '' || excludedServiceTypes.includes(row.product)) {
        return;
      }
      
      // Parse start and end dates using the safe date parsing function
      let startDate, endDate;
      try {
        startDate = safeParseDate(row.start);
        endDate = safeParseDate(row.end);
        
        // Skip if invalid dates
        if (!startDate || !endDate) {
          console.warn('Skipping row with invalid dates:', row);
          return;
        }
      } catch (error) {
        console.warn('Error parsing dates:', error, row);
        return;
      }
      
      // Calculate number of days in the period (ensure at least 1 day)
      const dayDiff = Math.max(1, Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)));
      
      // Parse USD amount (JSON API returns clean numeric strings)
      const totalCost = parseFloat(row.USD) || 0;
      
      // Calculate cost per day
      const costPerDay = totalCost / dayDiff;
      
      // Get service type
      const serviceType = row.product || 'Unknown';
      
      // Iterate through each day in the period (use UTC to avoid DST issues)
      let currentDate = new Date(startDate);
      while (currentDate <= endDate) {
        const dateKey = currentDate.toISOString().split('T')[0];

        if (!dailyCosts[dateKey]) {
          dailyCosts[dateKey] = {};
        }

        if (!dailyCosts[dateKey][serviceType]) {
          dailyCosts[dateKey][serviceType] = 0;
        }

        dailyCosts[dateKey][serviceType] += costPerDay;

        // Move to next day using UTC to avoid DST issues
        currentDate.setUTCDate(currentDate.getUTCDate() + 1);
      }
    });
    
    return dailyCosts;
  }
  
  // Define colors for different service types
  const COLORS = [
    '#8884d8', '#82ca9d', '#ffc658', '#ff8042', '#a4de6c',
    '#d0ed57', '#83a6ed', '#8dd1e1', '#a4262c', '#ec407a'
  ];

  // Safe formatter function for numbers
  const formatCurrency = (value) => {
    if (value === undefined || value === null) return '$0.00';
    return `$${Number(value).toFixed(2)}`;
  };
  
  // Safe percentage calculator
  const calculatePercentage = (value, total) => {
    if (!value || !total || total === 0) return '0.0';
    return ((value / total) * 100).toFixed(1);
  };
  
  // Format period for display (e.g., "2025-04" -> "April 2025")
  const formatPeriod = (period) => {
    if (!period) return '';
    const [year, month] = period.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1, 1);
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  // Format team URN for display
  const formatTeamUrn = (urn) => {
    if (!urn) return 'Unknown';
    const match = urn.match(/do:(team|teamgroup):(.+)/);
    if (match) {
      const [, type, id] = match;
      return `${type === 'teamgroup' ? 'Organization' : 'Team'}: ${id}`;
    }
    return urn;
  };

  // Handle period change
  const handlePeriodChange = (e) => {
    setSelectedPeriod(e.target.value);
  };

  // If no data, show message
  if (chartData.length === 0 && selectedPeriod) {
    return (
      <div>
        {selectedInvoice ? (
          // Show invoice date and team UUID instead of dropdown
          <div className="mb-4 bg-white border border-cf-gray-200 rounded-cf-md p-4 shadow-cf">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-cf-gray-500 mb-1">Date</label>
                <p className="text-sm font-medium text-cf-gray-900">{selectedInvoice.period}</p>
              </div>
              <div>
                <label className="block text-xs font-medium text-cf-gray-500 mb-1">Team UUID</label>
                <p className="text-sm font-mono text-cf-gray-900">{formatTeamUrn(selectedInvoice.account_urn)}</p>
              </div>
            </div>
          </div>
        ) : (
          availablePeriods.length > 0 && (
            <div className="mb-4">
              <label htmlFor="period-filter" className="block text-sm font-medium text-cf-gray-700 mb-2">
                Filter by Date Range
              </label>
              <select
                id="period-filter"
                value={selectedPeriod || ''}
                onChange={handlePeriodChange}
                className="block w-full max-w-xs px-3 py-2 border border-cf-gray-300 rounded-cf-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-sm text-cf-gray-900"
              >
                {availablePeriods.map(period => (
                  <option key={period} value={period}>
                    {formatPeriod(period)}
                  </option>
                ))}
              </select>
            </div>
          )
        )}
        <div className="bg-white border border-cf-gray-200 rounded-cf-md p-6 text-center shadow-cf">
          <p className="text-sm text-cf-gray-600">No cost data to display. This could be because there are no billable services in this period, or all services have been filtered out.</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {selectedInvoice ? (
        // Show invoice date and team UUID instead of dropdown
        <div className="mb-4 bg-white border border-cf-gray-200 rounded-cf-md p-4 shadow-cf">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-cf-gray-500 mb-1">Date</label>
              <p className="text-sm font-medium text-cf-gray-900">{selectedInvoice.period}</p>
            </div>
            <div>
              <label className="block text-xs font-medium text-cf-gray-500 mb-1">Team UUID</label>
              <p className="text-sm font-mono text-cf-gray-900">{formatTeamUrn(selectedInvoice.account_urn)}</p>
            </div>
          </div>
        </div>
      ) : (
        availablePeriods.length > 0 && (
          <div className="mb-4">
            <label htmlFor="period-filter" className="block text-sm font-medium text-cf-gray-700 mb-2">
              Filter by Date Range
            </label>
            <select
              id="period-filter"
              value={selectedPeriod || ''}
              onChange={handlePeriodChange}
              className="block w-full max-w-xs px-3 py-2 border border-cf-gray-300 rounded-cf-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-sm text-cf-gray-900"
            >
              {availablePeriods.map(period => (
                <option key={period} value={period}>
                  {formatPeriod(period)}
                </option>
              ))}
            </select>
          </div>
        )
      )}
      <div className="mb-6">
        <h2 className="text-lg font-semibold mb-2 text-cf-gray-900">Daily Costs by Service Type</h2>
        <p className="mb-4 text-sm text-cf-gray-600">Excluding Premier Support Subscription, Discounts, and Commitment Deal Surcharge</p>
        <div className="bg-white border border-cf-gray-200 p-4 rounded-cf-md shadow-cf">
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 70 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E8E8E8" />
              <XAxis
                dataKey="date"
                angle={-45}
                textAnchor="end"
                height={70}
                tick={{ fontSize: 12, fill: '#6B6B6B' }}
                stroke="#E8E8E8"
              />
              <YAxis
                label={{ value: 'Cost (USD)', angle: -90, position: 'insideLeft', fill: '#6B6B6B' }}
                tick={{ fontSize: 12, fill: '#6B6B6B' }}
                stroke="#E8E8E8"
              />
              <Tooltip
                formatter={(value) => formatCurrency(value)}
                labelFormatter={(label) => `Date: ${label}`}
                contentStyle={{
                  backgroundColor: 'white',
                  border: '1px solid #E8E8E8',
                  borderRadius: '3px',
                  fontSize: '12px'
                }}
              />
              <Legend wrapperStyle={{ fontSize: '12px' }} />
              {serviceTypes.map((serviceType, index) => (
                <Bar
                  key={serviceType}
                  dataKey={serviceType}
                  stackId="a"
                  fill={COLORS[index % COLORS.length]}
                  name={serviceType}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Summary Table */}
      <div className="mb-6">
        <h2 className="text-lg font-semibold mb-2 text-cf-gray-900">Summary by Service Type</h2>
        <div className="overflow-x-auto bg-white border border-cf-gray-200 rounded-cf-md shadow-cf">
          <table className="min-w-full">
            <thead className="bg-cf-gray-50 border-b border-cf-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-cf-gray-700 uppercase tracking-wider">Service Type</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-cf-gray-700 uppercase tracking-wider">Total Cost</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-cf-gray-700 uppercase tracking-wider">Percentage</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-cf-gray-200">
              {serviceTypes.map(type => (
                <tr key={type} className="hover:bg-cf-gray-50 transition-colors">
                  <td className="px-4 py-3 text-sm text-cf-gray-800">{type}</td>
                  <td className="px-4 py-3 text-sm text-right text-cf-gray-800">
                    {formatCurrency(totalCosts[type])}
                  </td>
                  <td className="px-4 py-3 text-sm text-right text-cf-gray-800">
                    {calculatePercentage(totalCosts[type], totalCosts.total)}%
                  </td>
                </tr>
              ))}
              <tr className="bg-cf-gray-50 font-semibold border-t-2 border-cf-gray-300">
                <td className="px-4 py-3 text-sm text-cf-gray-900">Total</td>
                <td className="px-4 py-3 text-sm text-right text-cf-gray-900">
                  {formatCurrency(totalCosts.total)}
                </td>
                <td className="px-4 py-3 text-sm text-right text-cf-gray-900">100%</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Daily Breakdown Table */}
      <div>
        <h2 className="text-lg font-semibold mb-2 text-cf-gray-900">Daily Cost Details</h2>
        <div className="overflow-x-auto bg-white border border-cf-gray-200 rounded-cf-md shadow-cf">
          <table className="min-w-full">
            <thead className="bg-cf-gray-50 border-b border-cf-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-cf-gray-700 uppercase tracking-wider">Date</th>
                {serviceTypes.map(type => (
                  <th key={type} className="px-4 py-3 text-right text-xs font-semibold text-cf-gray-700 uppercase tracking-wider">
                    {type}
                  </th>
                ))}
                <th className="px-4 py-3 text-right text-xs font-semibold text-cf-gray-700 uppercase tracking-wider">Total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-cf-gray-200">
              {chartData.map(day => (
                <tr key={day.date} className="hover:bg-cf-gray-50 transition-colors">
                  <td className="px-4 py-3 text-sm text-cf-gray-800">{day.date}</td>
                  {serviceTypes.map(type => (
                    <td key={type} className="px-4 py-3 text-sm text-right text-cf-gray-800">
                      {formatCurrency(day[type])}
                    </td>
                  ))}
                  <td className="px-4 py-3 text-sm text-right font-semibold text-cf-gray-900">
                    {formatCurrency(day.total)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default CostBreakdown;